/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import styles from './styles';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import { FilledInput, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { CancelPresentationRounded, DeleteRounded, SaveRounded } from '@mui/icons-material';
import SelectEditInputTextField from '../../SelectEditInputTextField';
import ModalDialog from '../../ModalDialog';
import Datagrid from '../../Datagrid';
import columns from './table_config';
import PropTypes from 'prop-types';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import useUnsavedChanges from '../../../../hooks/useUnsavedChanges';
import useUnitConversion from '../../../../hooks/useUnitConversion';
import { UNIT_SYSTEM } from '../../../../shared/config/hydraulics_constants';
import { addNewObservationRow, createObservation, deleteObservationRow, listObservation, updateObservation } from '../../../../store/actions/observation';
import { ANCHOR_POINT_TYPES, MAX_ROW_LIMITS } from '../../../../utils/constants';
import { COLORS } from '../../../../shared/config/colors';

const TargetModal = ({
  onCloseClick,
  isOpen,
  data,
  setTargetData,
  updateTargetData,
  onSave,
  anchorPoint,
  targetECD
}) => {
  const classes = styles();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const { unit, convertUnitSystem } = useUnitConversion();
  const observation = useSelector((state) => state.observation);
  const [anchorType, setAnchorTypes] = useState(ANCHOR_POINT_TYPES);
  const [targetLoader, setTargetLoader] = useState(false);
  const [localAnchorPoint, setLocalAnchorPoint] = useState('');
  const [localTargetECD, setLocalTargetECD] = useState('');
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, onCloseClick);

  useEffect(() => {
    dispatch(listObservation(data?.wellsInfoId));
  }, [isOpen]);

  useEffect(() => {
    if (observation?.data?.length && !observation.loading) {
      const extractObservationNames = observation.data.map(item => item.name);
      const updatedAnchorTypes = [...ANCHOR_POINT_TYPES, ...extractObservationNames];
      setProperties(observation.data);
      setAnchorTypes(updatedAnchorTypes);
      setLocalTargetECD(targetECD);
      if (!updatedAnchorTypes.includes(anchorPoint)) {
        if (!observation?.data.some(item => item.isDirty === true)) {
          dispatch(
            enqueueSnackbar(
              'Current Anchor Point is not available in the list. Please update',
              'error',
              new Date().getTime() + Math.random()
            )
          );
        }
        setLocalAnchorPoint('');
        setLocalTargetECD('');
      } else {
        setLocalAnchorPoint(anchorPoint);
      }
    } else {
      setProperties([]);
    }
  }, [observation?.data]);

  const handleAddObservation = () => {
    dispatch(addNewObservationRow());
  };

  useEffect(() => {
    if (properties[0]?.name === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'name', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'name');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const hasDuplicateDepthAndName = (arr, row) => {
    const seenMd = new Set();
    const seenNames = new Set();
    const seenCombinations = new Set();

    for (const item of arr) {
      const md = Number(item.md);
      const name = item.name?.toLowerCase()?.trim();
      const comboKey = `${md}-${name}`;

      const isReservedName = ANCHOR_POINT_TYPES.some(
        anchor => anchor.toLowerCase() === name
      );

      if (isReservedName) {
        dispatch(
          enqueueSnackbar(
            "The selected name already exists in target's Anchor Point dropdown",
            'error',
            new Date().getTime() + Math.random()
          )
        );
        setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        return true;
      }

      if (seenCombinations.has(comboKey)) {
        dispatch(
          enqueueSnackbar(
            'Name and Measured Depth combination already exists',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        return true;
      }

      if (seenMd.has(md)) {
        dispatch(
          enqueueSnackbar(
            'Measured Depth already exists',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        return true;
      }

      if (seenNames.has(name)) {
        dispatch(
          enqueueSnackbar(
            'Name already exists',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        return true;
      }

      seenMd.add(md);
      seenNames.add(name);
      seenCombinations.add(comboKey);
    }

    return false;
  };

  const checkObservationValue = (value) => {
    const row = value;
    const invalidFields = [];
    if (!row?.name) invalidFields.push('Name');
    if (row?.md === undefined || row?.md === null || row?.md === '') invalidFields.push('Measured depth');
    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return true;
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const validateProperties = (row) => {
    const { name } = row;
    if (typeof name !== 'string' || name.length > 15) {
      dispatch(
        enqueueSnackbar(
          'Name should be limited to 15 characters',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return true;
    }
    if (Number(row?.md) < 0 || Number(row?.md) > data?.run_details?.hole_depth) {
      dispatch(
        enqueueSnackbar(
          `Measured Depth should be between 0.0 and ${data?.run_details?.hole_depth}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return true;
    }
    return false;
  };

  const handleSave = (row) => {
    if (checkObservationValue(row)) {
      if (!hasDuplicateDepthAndName(properties, row) && !validateProperties(row)) {
        const convertedMd = unit !== UNIT_SYSTEM.US
          ? convertUnitSystem(row?.md, observation?.units?.md.conversionFactor)
          : Number(row?.md);
        const body = {
          name: row?.name,
          md: convertedMd
        };
        setInvalidRows(prev => ({ ...prev, [row._id]: false }));
        if (row?.isNewRow) {
          dispatch(createObservation(body, data?.wellsInfoId));
        } else {
          dispatch(updateObservation(body, data?.wellsInfoId, row?._id));
        }
      }
    }
  };

  const removeObservation = (row) => {
    if (row?.isNewRow) {
      const updatedObservation = properties.filter(item => item._id !== row?._id);
      setProperties(updatedObservation);
      dispatch(listObservation(data?.wellsInfoId));
    } else {
      if (row?.name === anchorPoint) {
        dispatch(
          enqueueSnackbar(
            'Deletion failed: The selected observation is currently set as the anchor point and cannot be removed.',
            'error',
            new Date().getTime() + Math.random()
          )
        );
      } else {
        dispatch(deleteObservationRow(row?._id, data?.wellsInfoId));
      }
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    onCloseClick();
  };

  const getModalTitle = () => {
    return (
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          {`${data?.name}`}
        </Grid>
        <Grid item>
          <IconButton
            onClick={closeHandler}
          >
            <CancelPresentationRounded fontSize='large' />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: typeof value === 'string' ? value : Number(value),
            isDirty
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  const renderValueTextCell = (params) => {
    return <SelectEditInputTextField {...params} updateData={updateData} />;
  };

  const renderActionCell = ({ row }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || observation.loading}
          onClick={() => {
            setTimeout(() => {
              handleSave(row);
            }, 100);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={observation.loading}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const checkTargetECD = () => {
    const minECD = observation?.units?.target_data?.target_ecd?.min;
    const maxECD = observation?.units?.target_data?.target_ecd?.max;
    const unit = observation?.units?.target_data?.target_ecd?.label;

    const isAnchorPointEmpty = localAnchorPoint === '' || localAnchorPoint === null || localAnchorPoint === undefined;
    const isTargetECDEmpty = localTargetECD === '' || localTargetECD === null || localTargetECD === undefined;

    if (isAnchorPointEmpty && isTargetECDEmpty) {
      dispatch(
        enqueueSnackbar(
          'Please select Anchor Point and enter Target ECD',
          'error',
          Date.now() + Math.random()
        )
      );
      return false;
    }

    if (isAnchorPointEmpty) {
      dispatch(
        enqueueSnackbar(
          'Please choose Anchor Point',
          'error',
          Date.now() + Math.random()
        )
      );
      return false;
    }

    if (isTargetECDEmpty) {
      dispatch(
        enqueueSnackbar(
          'Please enter Target ECD',
          'error',
          Date.now() + Math.random()
        )
      );
      return false;
    }

    const targetECDValue = Number(localTargetECD);
    if (isNaN(targetECDValue) || targetECDValue < minECD || targetECDValue > maxECD) {
      dispatch(
        enqueueSnackbar(
          `Target ECD must be a number between ${minECD} and ${maxECD} ${unit}`,
          'error',
          Date.now() + Math.random()
        )
      );
      return false;
    }

    return true;
  };

  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={onCloseClick}
      dialogClass={classes.dialogModal}
    >
      <Grid container>
        <Grid container item marginLeft='12px' marginTop='15px' marginBottom='5px'>
          <Grid>
            <Typography variant="h5">Well&apos;s Observation Points</Typography>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item marginLeft='12px' className={classes.customTool}>
            <Typography variant='button' fontWeight='700' onClick={handleAddObservation} className={properties?.length === MAX_ROW_LIMITS.WELLS_OBSERVATION || properties.some(item => item.isDirty === true) ? classes.disableIcon : ''}>+ Add</Typography>
          </Grid>
          <Grid item marginLeft='12px' className={classes.entryLabel}>
            <Typography variant='h6' color={COLORS.LIGHT_GREY} fontWeight='500'>{`(Note: Maximum ${MAX_ROW_LIMITS.WELLS_OBSERVATION} entries are allowed)`}</Typography>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Datagrid
            apiRef={apiRef}
            data={properties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem',
                fontWeight: '900 !important'
              },
              '& .MuiDataGrid-footerContainer': {
                display: 'none',
                height: '0px'
              },
            }}
            loading={observation?.loading || targetLoader}
            columns={columns(renderValueTextCell, renderValueCell, renderActionCell, observation?.units, unit)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            height={'22vh'}
            autoHeight={false}
            getRowId={(row) => row._id}
            getRowHeight={() => 'auto'}
            hideFooterPagination
            hideFooterSelectedRowCount
            getRowClassName={(params) => {
              return invalidRows[params.id] ? classes.invalidRow : '';
            }}
            pageSize={100}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
          />
        </Grid>
      </Grid>
      <Grid item className={classes.targetContainer}>
        <Grid container direction="column" margin={'6px'}>
          <Grid item>
            <Typography variant="h5">Run&apos;s Target Settings</Typography>
          </Grid>
          <Grid container item alignItems="center" marginTop={'10px'}>
            <Grid item xs={2}>
              <Typography fontWeight="700">Anchor Point</Typography>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth variant="filled" disabled={observation.loading || targetLoader}>
                <InputLabel id="anchor-point-label">Select Anchor Point</InputLabel>
                <Select
                  labelId="anchor-point-label"
                  id="anchor-point-select"
                  value={localAnchorPoint}
                  onChange={(e) => setLocalAnchorPoint(e.target.value)}
                  disableUnderline
                >
                  {anchorType?.map((item, index) => (
                    <MenuItem key={`item_${item}_${index}`} value={item}>{item}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container item alignItems="center" marginTop={'10px'}>
            <Grid item xs={2}>
              {localAnchorPoint !== 'None' && (
                <Typography fontWeight="700">
                  Target ECD ({observation?.units?.target_data?.target_ecd?.label})
                </Typography>
              )}
            </Grid>
            <Grid item xs={4}>
              {localAnchorPoint !== 'None' && (
                <FormControl fullWidth variant="filled" disabled={observation.loading || targetLoader}>
                  <InputLabel htmlFor="target-ecd">Enter Target ECD</InputLabel>
                  <FilledInput
                    id="target-ecd"
                    value={localTargetECD}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        setLocalTargetECD(value);
                      }
                    }}
                    disableUnderline
                  />
                </FormControl>
              )}
            </Grid>
            <Grid item xs={6} container justifyContent="flex-end">
              <IconButton
                variant="solid"
                onClick={() => {
                  const isNone = localAnchorPoint === 'None';

                  setTargetLoader(true);
                  setTimeout(() => setTargetLoader(false), 500);

                  let updatedTargetData = {};

                  if (!isNone && !checkTargetECD()) {
                    return;
                  }

                  if (!isNone) {
                    updatedTargetData = {
                      anchor_point: localAnchorPoint,
                      target_ecd: Number(localTargetECD)
                    };
                  }

                  setTargetData(updatedTargetData);
                  onCloseClick();
                  updateTargetData(data);
                  onSave(localAnchorPoint, localTargetECD);
                }}
              >
                <SaveRounded fontSize='large' />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {
        deleteAction && <DeleteConfirmationModal
          open={deleteAction}
          handleDelete={removeObservation}
          handleClose={setDeleteAction}
          deleteRow={deleteRow}
        />
      }
      <UnsavedChangesModal />
    </ModalDialog>
  );
};
TargetModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  selectedJob: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.any,
  setTargetData: PropTypes.func,
  updateTargetData: PropTypes.func,
  anchorPoint: PropTypes.any,
  targetECD: PropTypes.any,
  onSave: PropTypes.func
};
TargetModal.defaultProps = {
  selectedJob: {}
};

export default TargetModal;
